import { rasterSrc } from "@/generated/raster-images";
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, Section } from "@/components/site-blocks";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/complaints-policy")({
  head: () => ({
    ...pageSeo({
      path: "/complaints-policy",
      title: "Complaints Policy | CRS Energy",
      description:
        "How CRS Energy identifies, records and handles complaints as an Introducer Appointed Representative of HTG Finance Ltd.",
    }),
  }),
  component: ComplaintsPolicyPage,
});

function ComplaintsPolicyPage() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Complaints Policy"
        description="How CRS Energy identifies, records, and handles complaints in relation to its regulated introducing activities."
        image={rasterSrc("stock/photo-1573164574511-73c773193279")}
        imageAlt=""
      />
      <Section>
        <div className="max-w-3xl space-y-8 text-muted-foreground">
          <p className="text-lg font-semibold text-foreground">
            CRS Energy - Complaints Policy
          </p>

          <section>
            <h2 className="text-xl font-semibold text-foreground">1. Purpose</h2>
            <p className="mt-2">
              This policy sets out how CRS Energy identifies, records, and handles complaints in
              relation to its regulated introducing activities as an Introducer Appointed
              Representative of HTG Finance Ltd ('Hometree'), the principal firm.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">2. Scope</h2>
            <p className="mt-2">This policy applies to all complaints received from retail customers relating to:</p>
            <ul className="mt-2 list-disc pl-6 space-y-1">
              <li>Introducing activities.</li>
              <li>The product or services provided by Hometree.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">3. Definition of a Complaint</h2>
            <p className="mt-2">
              The definition of a complaint is as follows, in accordance with the FCA Handbook:
            </p>
            <p className="mt-2">
              Any oral or written expression of dissatisfaction, whether justified or not, from, or
              on behalf of, a person about the provision of, or failure to provide, a financial
              service or a redress determination, which:
            </p>
            <ul className="mt-2 list-[lower-alpha] pl-6 space-y-1">
              <li>
                alleges that the complainant has suffered (or may suffer) financial loss, material
                distress or material inconvenience; and
              </li>
              <li>
                relates to an activity of that firm, or of any other firm with whom that firm has
                some connection in marketing or providing financial services or products, which
                comes under the jurisdiction of the Financial Ombudsman Service.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">4. How to Complain</h2>
            <p className="mt-2">Customers can raise a complaint with CRS Energy by:</p>
            <ul className="mt-2 list-disc pl-6 space-y-1">
              <li>Phone: 01656 253194</li>
              <li>Email: [[INSERT CRS ENERGY COMPLAINTS EMAIL]]</li>
            </ul>
            <p className="mt-3">
              Complaints about Hometree Finance's own products can also be raised directly with
              Hometree Finance at hello@hometreefinance.co.uk or 0800 987 1789.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">5. Handling of Complaints</h2>
            <ul className="mt-2 list-disc pl-6 space-y-1">
              <li>Upon receipt of a complaint, CRS Energy promptly forwards complaints to Hometree.</li>
              <li>CRS Energy will cooperate with Hometree in investigating and resolving complaints.</li>
              <li>
                CRS Energy does not investigate or determine the outcome of complaints relating to
                Hometree's regulated products - this is handled by Hometree as principal firm.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              6. Timescales and the Financial Ombudsman Service
            </h2>
            <p className="mt-2">
              [[CONFIRM WITH HOMETREE FINANCE BEFORE PUBLISHING: exact final-response timescale and
              FOS referral wording. Suggested placeholder text - Hometree Finance, as principal
              firm, is responsible for issuing a final response to complaints, normally within 8
              weeks. If a customer is unhappy with the final response, or has not received one
              within 8 weeks, they may be entitled to refer their complaint to the Financial
              Ombudsman Service (FOS) free of charge.]]
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">7. Reporting and Record Keeping</h2>
            <p className="mt-2">
              CRS Energy maintains adequate records of complaints and provides information to
              Hometree as required for FCA reporting and oversight.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">8. Training</h2>
            <p className="mt-2">
              Relevant staff receive appropriate training on recognising a complaint and the
              complaint forwarding process.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">9. Review</h2>
            <p className="mt-2">This policy is reviewed at least annually.</p>
            <p className="mt-2">Last reviewed: August 2026</p>
          </section>
        </div>
      </Section>
    </>
  );
}
